import { createContext, useState } from "react";

export const paginationContext = createContext();

const { Provider } = paginationContext;

const _initialState = {
  disabled: false,
  /**
   * Max page number (upper limit of pagination).
   * Could be set to null when the real value is unknown. If so, no upper limit should be enforced.
   */
  pageCount: 0,
  /**
   *  Current page number.
   */
  pageNo: 0,
};

/**
 * Returns the value of `number` clamped between the values of `min` and `max` (both inclusive).
 * If `max` is omitted (or null) the upper limit used is the value of Number.MAX_SAFE_INTEGER.
 * 
 * @param {Number} number
 * @param {Number} min
 * @param {Number} max
 */
const clamp = (number, min, max) => Math.max(min, Math.min(number, max ?? Number.MAX_SAFE_INTEGER));

const PaginationProvider = ({ children }) => {
  const [state, setState] = useState(_initialState);

  /**
   * Initialize `pageCount` and `pageNo`.
   *
   * @param {Number} pageCount
   * @param {Number} pageNo
   */
  const initialize = ({ pageCount, pageNo } = {}) => {
    setState({
      ..._initialState,
      pageCount,
      pageNo,
    });
  };

  /**
   * Set the current page number.
   * 
   * @param {Number} pageNo
   */
  const changePage = (pageNo) => {
    setState(prev => ({
      ...prev,
      pageNo: clamp(pageNo, 1, prev.pageCount),
    }));
  };

  const disable = () => {
    setState(prev => ({
      ...prev,
      disabled: true,
    }));
  };

  const enable = () => {
    setState(prev => ({
      ...prev,
      disabled: false,
    }));
  };

  /**
   * Increase the current page number by one.
   */
  const nextPage = () => {
    setState(prev => ({
      ...prev,
      pageNo: clamp(prev.pageNo + 1, 1, prev.pageCount),
    }));
  };

  /**
   * Decrease the current page number by one.
   */
  const previousPage = () => {
    setState(prev => ({
      ...prev,
      pageNo: clamp(prev.pageNo - 1, 1, prev.pageCount),
    }));
  };

  return (
    <Provider value={{
      ...state,
      /**
       * Indicates when `pageNo` has reached its maximum allowed value.
       */
      atMaxLimit: (state.pageNo >= (state.pageCount ?? Number.MAX_SAFE_INTEGER)),
      /**
       * Indicates when `pageNo` has reached its minimum allowed value.
       */
      atMinLimit: (state.pageNo <= 1),
      changePage,
      disable,
      enable,
      initialize,
      nextPage,
      previousPage,
    }}>
      {children}
    </Provider>
  );
};

export default PaginationProvider;
